import { useEffect, useState } from "react";
import {
  ArrowSquareOut,
  Stack,
  Notebook,
  ShieldCheck,
  Gauge,
  GitBranch,
  CheckCircle,
  Hammer,
  CheckSquare,
  ArrowUUpLeft,
} from "@phosphor-icons/react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import CardDecisionFooter from "./CardDecisionFooter";
import WorkbookInPaneRenderer from "./WorkbookInPaneRenderer";
import { fetchWorkbookRaw, submitViewAuditTouch, revertViewAuditTouch } from "../lib/api";
import { XrefText, findRelatedItems } from "../lib/xrefText";

/**
 * RoadmapDetailPane — right column of the master-detail layout. Dispatches
 * to a type-specific renderer based on `item.type`.
 *
 * Phase 12E (cycle-5c+25-fix8).
 */

const GITHUB_REPO_BASE = "https://github.com/lifttechanz/lifttech-shared/blob/main";

function buildDeepLink(sourcePath) {
  if (!sourcePath) return null;
  const cleaned = sourcePath.replace(/^\/+/, "");
  return `${GITHUB_REPO_BASE}/${cleaned}`;
}

const TYPE_LABEL = {
  promise:    { icon: Stack,       label: "Promise",    color: "hsl(var(--primary))" },
  workbook:   { icon: Notebook,    label: "Workbook",   color: "hsl(var(--status-warning))" },
  safety_net: { icon: ShieldCheck, label: "Safety-net", color: "hsl(var(--status-critical))" },
  feature:    { icon: GitBranch,   label: "Feature",    color: "hsl(var(--foreground))" },
  activity:   { icon: Gauge,       label: "Activity",   color: "hsl(var(--muted-foreground))" },
  ledger:     { icon: CheckCircle, label: "Decided",    color: "hsl(var(--status-success))" },
};

function DetailHeader({ item }) {
  const t = TYPE_LABEL[item.type] || TYPE_LABEL.activity;
  const Icon = t.icon;
  const deepLink = buildDeepLink(item.source_path);
  return (
    <div className="flex items-start gap-2 sm:gap-3 mb-3 pb-3 border-b" style={{ borderColor: "hsl(var(--border))" }}>
      <div
        className="flex h-8 w-8 items-center justify-center rounded-md shrink-0"
        style={{ background: `${t.color.replace("hsl(", "hsl(").replace(")", " / 0.10)")}`, color: t.color }}
      >
        <Icon size={16} weight="bold" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mb-0.5">
          <span
            className="text-mono"
            style={{
              fontSize: "0.6rem",
              color: t.color,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              fontWeight: 700,
            }}
          >
            {t.label}
          </span>
          {item.tier && (
            <span
              className="text-mono"
              style={{ fontSize: "0.6rem", color: "hsl(var(--muted-foreground))", fontWeight: 600 }}
            >
              {item.tier}
            </span>
          )}
          {item.agents.length > 0 && (
            <span
              className="text-mono"
              style={{ fontSize: "0.6rem", color: "hsl(var(--muted-foreground))" }}
            >
              {item.agents.map((a) => a.toUpperCase()).join(" · ")}
            </span>
          )}
        </div>
        <h2
          className="text-base sm:text-lg font-semibold leading-snug break-words"
          style={{ color: "hsl(var(--foreground))" }}
          data-testid="roadmap-detail-title"
        >
          {item.ref && (
            <span
              className="text-mono mr-2 rounded-md border px-1.5 py-0.5 align-middle"
              style={{
                fontSize: "0.72rem",
                fontWeight: 700,
                color: "hsl(var(--primary))",
                background: "hsl(var(--primary) / 0.08)",
                borderColor: "hsl(var(--primary) / 0.4)",
              }}
              data-testid="roadmap-detail-ref"
              aria-label={`Card reference ${item.ref}`}
            >
              {item.ref}
            </span>
          )}
          {item.title}
        </h2>
      </div>
      {deepLink && (
        <a
          href={deepLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-mono rounded-md border px-2 py-1 shrink-0 transition-colors hover:border-[hsl(var(--primary))]"
          style={{
            fontSize: "0.65rem",
            color: "hsl(var(--foreground))",
            borderColor: "hsl(var(--border))",
            fontWeight: 600,
          }}
          data-testid="roadmap-detail-readmore"
        >
          <ArrowSquareOut size={10} />
          Canon
        </a>
      )}
    </div>
  );
}

// ── Phase 14 / Batch 3 / cycle-5c+25-fix20 — staleness summary + touch button ─

const FRESHNESS_STYLES = {
  fresh: {
    label: "Fresh",
    dotBg: "hsl(var(--status-success))",
    chipBg: "hsl(var(--status-success) / 0.10)",
    chipFg: "hsl(var(--status-success))",
    chipBorder: "hsl(var(--status-success) / 0.40)",
  },
  aging: {
    label: "Aging",
    dotBg: "hsl(var(--status-warning))",
    chipBg: "hsl(var(--status-warning) / 0.10)",
    chipFg: "hsl(var(--status-warning))",
    chipBorder: "hsl(var(--status-warning) / 0.40)",
  },
  stale: {
    label: "Stale",
    dotBg: "hsl(var(--status-critical))",
    chipBg: "hsl(var(--status-critical) / 0.10)",
    chipFg: "hsl(var(--status-critical))",
    chipBorder: "hsl(var(--status-critical) / 0.40)",
  },
  never: {
    label: "Never touched",
    dotBg: "hsl(var(--muted-foreground))",
    chipBg: "hsl(var(--muted) / 0.50)",
    chipFg: "hsl(var(--muted-foreground))",
    chipBorder: "hsl(var(--border))",
  },
};

function formatRelative(iso) {
  if (!iso) return "never";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "unknown";
  const days = Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24));
  if (days <= 0) return "today";
  if (days === 1) return "1 day ago";
  if (days < 31) return `${days} days ago`;
  if (days < 365) {
    const months = Math.floor(days / 30);
    return months === 1 ? "1 month ago" : `${months} months ago`;
  }
  const years = Math.floor(days / 365);
  return years === 1 ? "1 year ago" : `${years} years ago`;
}

function StalenessSummary({ item, onViewAuditTouched }) {
  const { isAuthenticated, user, getIdTokenClaims } = useAuth0();
  const [busy, setBusy] = useState(false);
  const [touchError, setTouchError] = useState(null);
  // Track local override of "did the current viewer just touch this?" so the
  // button re-renders to "No work done" without a parent refetch round-trip.
  // null = follow item.viewer_email; true = locally just-touched; false = locally just-reverted.
  const [localTouchedOverride, setLocalTouchedOverride] = useState(null);

  const tier = item.freshness_tier || "never";
  const style = FRESHNESS_STYLES[tier] || FRESHNESS_STYLES.never;
  const itemTypeOriginal = item.id.includes(":") ? item.id.split(":")[0] : item.type;
  const itemIdOriginal = item.id.includes(":")
    ? item.id.substring(item.id.indexOf(":") + 1)
    : item.raw?.row_id || item.raw?.id || item.id;

  // cycle-5c+25-fix25 (Owner-direct 2026-05-26): auto-touch on detail-pane
  // OPEN. Owner self-corrected Q2 from (b) "explicit-only" → (a) "auto-touch
  // on open"; the explicit button is INVERTED into a "No work done" revert
  // affordance for accidental opens. Auto-touch is idempotent: the backend
  // accepts multiple touches per (item, viewer) so re-mounting the pane on
  // the same card doesn't error. The reset gate `item.id` ensures we only
  // touch when the user actually navigates to a NEW card.
  useEffect(() => {
    let cancelled = false;
    setLocalTouchedOverride(null);
    setTouchError(null);
    if (!isAuthenticated || !item?.id) return;
    (async () => {
      try {
        const claims = await getIdTokenClaims();
        const token = claims?.__raw;
        if (!token || cancelled) return;
        await submitViewAuditTouch(itemTypeOriginal, itemIdOriginal, token);
        if (cancelled) return;
        setLocalTouchedOverride(true);
        if (typeof onViewAuditTouched === "function") {
          await onViewAuditTouched();
        }
      } catch (e) {
        if (cancelled) return;
        // Auto-touch is best-effort — surface error but don't block the pane.
        // eslint-disable-next-line no-console
        console.warn("Auto-touch on detail-pane open failed", e);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id, isAuthenticated]);

  // Was the latest view_audit touch made BY THE CURRENT VIEWER? Drives the
  // button-label flip: "I worked on this" (no current-viewer touch yet) vs
  // "No work done" (current viewer has a touch they can revert).
  const currentViewerEmail = user?.email || null;
  const latestByCurrentViewer =
    item.viewer_email &&
    currentViewerEmail &&
    item.viewer_email.toLowerCase() === currentViewerEmail.toLowerCase();
  const showRevert =
    localTouchedOverride === true ||
    (localTouchedOverride !== false && latestByCurrentViewer);

  const handleTouchOrRevert = async () => {
    if (!isAuthenticated) {
      setTouchError("Sign in first.");
      return;
    }
    setBusy(true);
    setTouchError(null);
    try {
      const claims = await getIdTokenClaims();
      const token = claims?.__raw;
      if (!token) throw new Error("Could not retrieve ID token.");
      if (showRevert) {
        await revertViewAuditTouch(itemTypeOriginal, itemIdOriginal, token);
        setLocalTouchedOverride(false);
      } else {
        await submitViewAuditTouch(itemTypeOriginal, itemIdOriginal, token);
        setLocalTouchedOverride(true);
      }
      if (typeof onViewAuditTouched === "function") {
        await onViewAuditTouched();
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Touch action failed";
      setTouchError(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3 pb-3 border-b"
      style={{ borderColor: "hsl(var(--border))" }}
      data-testid="roadmap-detail-staleness"
    >
      <span
        className="inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-mono shrink-0"
        style={{
          fontSize: "0.6rem",
          fontWeight: 700,
          color: style.chipFg,
          background: style.chipBg,
          borderColor: style.chipBorder,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
        data-testid={`roadmap-detail-freshness-${tier}`}
      >
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{ background: style.dotBg }}
          aria-hidden="true"
        />
        {style.label}
      </span>
      <span
        className="text-xs"
        style={{ color: "hsl(var(--muted-foreground))" }}
        data-testid="roadmap-detail-last-worked"
      >
        Last worked: <strong style={{ color: "hsl(var(--foreground))" }}>{formatRelative(item.last_worked_at)}</strong>
        {item.work_source && (
          <span style={{ color: "hsl(var(--muted-foreground))" }}>
            {" "}({item.work_source === "workbook_pick_ack" ? "pick-ack" : "decision"})
          </span>
        )}
      </span>
      <span
        className="text-xs"
        style={{ color: "hsl(var(--muted-foreground))" }}
        data-testid="roadmap-detail-last-viewed"
      >
        Last touched: <strong style={{ color: "hsl(var(--foreground))" }}>{formatRelative(item.last_viewed_at)}</strong>
        {showRevert && (
          <span
            className="ml-1 text-mono"
            style={{ fontSize: "0.55rem", color: "hsl(var(--status-success))" }}
          >
            (auto-touched on open)
          </span>
        )}
      </span>
      <div className="hidden sm:block flex-1" />
      <button
        type="button"
        onClick={handleTouchOrRevert}
        disabled={busy || !isAuthenticated}
        className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-mono transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed hover:border-[hsl(var(--primary))] w-full sm:w-auto justify-center"
        style={{
          fontSize: "0.65rem",
          fontWeight: 700,
          background: showRevert
            ? "hsl(var(--status-warning) / 0.10)"
            : "hsl(var(--background))",
          borderColor: showRevert
            ? "hsl(var(--status-warning) / 0.45)"
            : "hsl(var(--border))",
          color: showRevert
            ? "hsl(var(--status-warning))"
            : "hsl(var(--foreground))",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
        data-testid="roadmap-detail-touch-button"
        title={
          isAuthenticated
            ? showRevert
              ? "Revert the auto-touch — I didn't actually work on this (accidental open / not engaged)"
              : "Record that I just worked on this card"
            : "Sign in to record a touch"
        }
      >
        {showRevert ? (
          <>
            <ArrowUUpLeft size={12} weight="bold" />
            {busy ? "Reverting…" : "No work done — revert"}
          </>
        ) : (
          <>
            <Hammer size={12} weight="bold" />
            {busy ? "Touching…" : "I worked on this"}
          </>
        )}
      </button>
      {touchError && (
        <span
          className="text-xs w-full"
          style={{ color: "hsl(var(--status-critical))" }}
          data-testid="roadmap-detail-touch-error"
        >
          {touchError}
        </span>
      )}
    </div>
  );
}

function GenericDetailBody({ item, onDecisionSubmitted, allItems }) {
  return (
    <div>
      {item.summary && (
        <p
          className="text-sm leading-relaxed mb-3"
          style={{ color: "hsl(var(--foreground))" }}
        >
          <XrefText text={item.summary} />
        </p>
      )}
      {/* Extra body for promises: render notes if separate from summary. */}
      {item.type === "promise" && item.raw?.gate && (
        <div className="mb-3">
          <span
            className="text-mono"
            style={{
              fontSize: "0.6rem",
              color: "hsl(var(--muted-foreground))",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              fontWeight: 700,
            }}
          >
            Gate:
          </span>
          <p
            className="text-xs leading-relaxed mt-0.5"
            style={{ color: "hsl(var(--foreground))" }}
          >
            <XrefText text={item.raw.gate} />
          </p>
        </div>
      )}
      {item.type === "safety_net" && item.raw?.soak_window && (
        <div className="mb-3">
          <span
            className="text-mono"
            style={{
              fontSize: "0.6rem",
              color: "hsl(var(--muted-foreground))",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              fontWeight: 700,
            }}
          >
            Soak window:
          </span>
          <p className="text-xs mt-0.5" style={{ color: "hsl(var(--foreground))" }}>
            <XrefText text={item.raw.soak_window} />
          </p>
        </div>
      )}
      {/* cycle-5c+25-fix25 — Related items by slug-stem match. Surfaces e.g.
          the RI-onboarding-workbook-PLAIN when viewing the RI-onboarding-phase-A
          promise, even though neither row explicitly references the other by
          [Cn] code. Owner-direct: *"if it says read the 7-question workbook
          then link to it.. OR at least list the card number so i can quickly
          look it up."* */}
      <RelatedItemsBlock item={item} allItems={allItems} />
      {/* Inline decision form (auto-hides when no §1.Q sub-block). */}
      {item.type !== "workbook" && item.type !== "ledger" && (
        <CardDecisionFooter
          itemId={item.raw?.row_id || item.raw?.id || item.id.split(":")[1]}
          itemType={item.type}
          itemTitle={item.title}
          sourcePath={item.source_path}
          lastDecision={item.last_decision}
          onDecisionSubmitted={onDecisionSubmitted}
        />
      )}
    </div>
  );
}

function WorkbookDetailBody({ item, allItems, onDecisionSubmitted }) {
  const workbookId = item.raw?.id || item.id.split(":")[1];
  const [wbData, setWbData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setWbData(null);
    fetchWorkbookRaw(workbookId)
      .then((data) => {
        if (cancelled) return;
        setWbData(data);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e.message || "Failed to load workbook");
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [workbookId]);

  if (loading) {
    return (
      <div className="surface-card animate-pulse h-64" />
    );
  }
  if (error) {
    return (
      <div
        className="surface-card p-4"
        style={{ borderColor: "hsl(var(--status-critical) / 0.5)", background: "hsl(var(--status-critical) / 0.05)" }}
      >
        <p
          className="text-sm font-semibold"
          style={{ color: "hsl(var(--status-critical))" }}
        >
          Couldn't load workbook
        </p>
        <p
          className="text-xs mt-1"
          style={{ color: "hsl(var(--muted-foreground))" }}
        >
          {error}
        </p>
      </div>
    );
  }
  return (
    <>
      <RelatedItemsBlock item={item} allItems={allItems} />
      <WorkbookInPaneRenderer
        workbookId={workbookId}
        content={wbData.markdown}
        parsed={wbData.parsed}
        onSubmitted={onDecisionSubmitted}
      />
    </>
  );
}

export default function RoadmapDetailPane({
  item,
  allItems,
  onDecisionSubmitted,
  onViewAuditTouched,
}) {
  if (!item) return null;
  return (
    <div
      className="surface-card p-3 sm:p-5"
      data-testid={`roadmap-detail-pane-${item.id}`}
    >
      <DetailHeader item={item} />
      <StalenessSummary item={item} onViewAuditTouched={onViewAuditTouched} />
      {item.type === "workbook" ? (
        <WorkbookDetailBody item={item} allItems={allItems} onDecisionSubmitted={onDecisionSubmitted} />
      ) : (
        <GenericDetailBody item={item} allItems={allItems} onDecisionSubmitted={onDecisionSubmitted} />
      )}
    </div>
  );
}

/**
 * cycle-5c+25-fix25 — surfaces sibling cards by slug-stem match.
 *
 * Owner-direct ([C16] example 2026-05-26): viewing a card should make
 * related workbooks/promises one click away even when neither row
 * explicitly names the other by [Cn] code in the prose.
 *
 * Uses `findRelatedItems` from `lib/xrefText.js` which extracts non-stopword
 * slug tokens from the current item's row_id and matches them against the
 * full canon. Already-linked refs (mentioned inline via XrefText in the
 * row's notes/gate text) are excluded to avoid duplication.
 */
function RelatedItemsBlock({ item, allItems }) {
  const navigate = useNavigate();
  const related = (() => {
    if (!Array.isArray(allItems)) return [];
    return findRelatedItems(item, allItems, 5);
  })();
  if (related.length === 0) return null;
  return (
    <div className="mb-4 mt-1">
      <span
        className="text-mono"
        style={{
          fontSize: "0.6rem",
          color: "hsl(var(--muted-foreground))",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontWeight: 700,
        }}
      >
        Related (slug-match):
      </span>
      <div className="mt-1.5 flex flex-col gap-1">
        {related.map((r) => {
          const originalId = r.id?.includes(":")
            ? r.id.substring(r.id.indexOf(":") + 1)
            : r.id;
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => navigate(`/?item=${encodeURIComponent(r.id)}`)}
              className="inline-flex items-center gap-2 w-full text-left rounded-md border px-2 py-1.5 transition-colors hover:bg-[hsl(var(--muted)/0.40)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              style={{ borderColor: "hsl(var(--border))" }}
              data-testid={`related-item-${originalId}`}
            >
              {r.ref && (
                <span
                  className="text-mono shrink-0"
                  style={{
                    fontSize: "0.6rem",
                    fontWeight: 700,
                    color: "hsl(var(--primary))",
                    background: "hsl(var(--primary) / 0.12)",
                    borderRadius: "0.25rem",
                    padding: "0.05rem 0.35rem",
                  }}
                >
                  [{r.ref}]
                </span>
              )}
              <span
                className="text-mono shrink-0"
                style={{
                  fontSize: "0.55rem",
                  color: "hsl(var(--muted-foreground))",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                {r.type}
              </span>
              <span
                className="text-xs flex-1 truncate min-w-0"
                style={{ color: "hsl(var(--foreground))" }}
              >
                {r.title}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
